import { useCallback, useMemo } from "react";
import type { ChangeEvent } from "react";
import OutlinedTextField from "components/Inputs/OutlinedTextField";
import MuiMenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import { useTranslation } from "react-i18next";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import Typography from "components/Typography";
import { ProfileType } from "components/charting/hooks/useProfileTypeQueryState";
import { styled } from "@mui/material";

interface SelectProfileTypeProps {
  value: ProfileType;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  "& .MuiListItemText-primary": { fontSize: theme.typography.TextS.fontSize },
  "& .MuiListItemText-secondary": {
    color: theme.palette.grey[120],
    fontSize: theme.typography.TextXS.fontSize,
  },
  "&.Mui-selected .MuiListItemText-secondary": { color: theme.palette.grey[40] },
}));

interface Option {
  value: ProfileType;
  label: string;
  description: string;
}

function SelectProfileType(props: SelectProfileTypeProps) {
  const { value, onChange, disabled } = props;
  const { t } = useTranslation();

  const options: Option[] = useMemo(
    () => [
      {
        value: ProfileType.Velocity,
        label: t("global.velocity_label"),
        description: t("athlete_profile.velocity_profile_select_description"),
      },
      {
        value: ProfileType.Power,
        label: t("global.power_label"),
        description: t("athlete_profile.power_profile_select_description"),
      },
    ],
    [t]
  );

  const renderValue = useCallback(
    (selected: unknown) => (
      <Typography variant="TextS">
        {options.find((option) => option.value === (selected as ProfileType))?.label}
      </Typography>
    ),
    [options]
  );

  const memoSlotProps = useMemo(
    () =>
      ({
        select: {
          sx: { minWidth: "140px" },
          renderValue,
          MenuProps: {
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
          },
        },
      }) as const,
    [renderValue]
  );

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => onChange(event), [onChange]);

  return (
    <ThemeProvider2024>
      <OutlinedTextField
        size="small"
        select
        label={t("athlete_profile.profile_type_label")}
        value={value}
        disabled={disabled}
        slotProps={memoSlotProps}
        aria-label={t("athlete_profile.profile_type_aria_label")}
        onChange={handleChange}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <ListItemText primary={option.label} secondary={option.description} />
          </MenuItem>
        ))}
      </OutlinedTextField>
    </ThemeProvider2024>
  );
}

export default SelectProfileType;
